import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppShell,
  Box,
  Burger,
  Group,
  LoadingOverlay,
  Overlay,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useAdminAuth } from "../context/AdminAuthContext";
import { AdminSidebar } from "./AdminSidebar";
import { OwnerChatDrawer } from "./OwnerChatDrawer";
import { ShopVisibilityPicker } from "./ShopVisibilityPicker";
import { useHeaderRightSlot } from "../context/HeaderSlotContext";
import { RAIL_WIDTH, SIDEBAR_BG, SIDEBAR_WIDTH } from "../theme";

const OWNER_ROUTES = ["/admin/dashboard", "/admin/conversations", "/admin/chargers", "/admin/reviews", "/admin/insights", "/admin/team"];
const HEADER_HEIGHT = 56;

export function AdminLayout() {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  // Desktop-only: collapse the left drawer to a thin rail (persisted).
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("admin_nav_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem("admin_nav_collapsed", next ? "1" : "0");
    return next;
  });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/admin/login", { replace: true, state: { from: location.pathname + location.search } });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return <LoadingOverlay visible loaderProps={{ type: "bars" }} />;
  }

  if (!isAuthenticated) {
    return null;
  }

  const isOwnerRoute = OWNER_ROUTES.some((p) => location.pathname.startsWith(p));

  return (
    <AppShell
      navbar={{
        // Desktop (sm+) narrows to the rail when collapsed; mobile drawer stays full.
        width: { base: SIDEBAR_WIDTH, sm: collapsed ? RAIL_WIDTH : SIDEBAR_WIDTH },
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened },
      }}
      header={isOwnerRoute ? { height: HEADER_HEIGHT } : undefined}
      padding={{ base: "md", sm: "lg", md: "xl" }}
      styles={{
        main: {
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {mobileOpened && (
        <Overlay
          onClick={toggleMobile}
          backgroundOpacity={0.5}
          zIndex={99}
          hiddenFrom="sm"
          fixed
        />
      )}

      {isOwnerRoute && <AdminGlobalHeader />}

      <AppShell.Navbar
        style={{
          backgroundColor: SIDEBAR_BG,
          zIndex: 100,
          borderRight: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <AdminSidebar onClose={toggleMobile} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </AppShell.Navbar>

      <AppShell.Main>
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <Group hiddenFrom="sm" mb="xs">
            <Burger
              opened={mobileOpened}
              onClick={toggleMobile}
              size="sm"
              color="var(--mantine-color-dark-4)"
            />
          </Group>
          <Outlet />
        </Box>
      </AppShell.Main>
      {isOwnerRoute && <OwnerChatDrawer />}
    </AppShell>
  );
}

function AdminGlobalHeader() {
  const right = useHeaderRightSlot();
  return (
    <AppShell.Header
      style={{
        backgroundColor: "white",
        borderBottom: "1px solid var(--mantine-color-gray-2)",
        display: "flex",
        alignItems: "center",
        paddingInline: 16,
        zIndex: 99,
      }}
    >
      <Group justify="space-between" align="center" w="100%" wrap="nowrap" gap={12}>
        <ShopVisibilityPicker variant="header" />
        <Box style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end", overflow: "auto" }}>
          {right}
        </Box>
      </Group>
    </AppShell.Header>
  );
}
